import * as React from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Box, Grid, TextField, FormControlLabel, Switch,
    MenuItem, Button, IconButton, Stepper, Step, StepLabel,
    InputAdornment,
    Typography,
    Chip,
    Checkbox,
    FormGroup,
    Alert,
    Card,
    Divider,
    Stack,
    Paper,
    alpha
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SaveIcon from '@mui/icons-material/Save';

// Basic step icons
import BusinessIcon from '@mui/icons-material/Business';
import DescriptionIcon from '@mui/icons-material/Description';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import GroupsIcon from '@mui/icons-material/Groups';
import TagIcon from '@mui/icons-material/Tag';

// Contact step icons
import PublicIcon from '@mui/icons-material/Public';
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import PhoneIcon from '@mui/icons-material/Phone';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import AttachEmailIcon from '@mui/icons-material/AttachEmail';
import { CertificatesMultiSelect } from './controls/CertificatesMultiSelect';
import { MarketplaceConsentsEditor } from './controls/MarketplaceConsentsEditor';

// Stepper icons
import ContactMailIcon from '@mui/icons-material/ContactMail';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import ShareIcon from '@mui/icons-material/Share';
import StorefrontIcon from '@mui/icons-material/Storefront';
import type { StepIconProps } from '@mui/material/StepIcon';

import Autocomplete from '@mui/material/Autocomplete';

import LocationCityIcon from '@mui/icons-material/LocationCity';
import MapIcon from '@mui/icons-material/Map';
import NumbersIcon from '@mui/icons-material/Numbers';
import ApartmentIcon from '@mui/icons-material/Apartment';
import MarkunreadMailboxIcon from '@mui/icons-material/MarkunreadMailbox';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import YouTubeIcon from '@mui/icons-material/YouTube';
import InstagramIcon from '@mui/icons-material/Instagram';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import VerifiedIcon from '@mui/icons-material/Verified';
import RedditIcon from "@mui/icons-material/Reddit";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import LinkIcon from "@mui/icons-material/Link";

import { CompanyConsent, CompanyCountryMarket, CompanyMarket, CompanyQuickTag, updateCompanyProfile, type CompanyProfile } from './companyProfileApi'; // <-- adjust path
import { TimeZoneSelect } from './controls/TimeZoneSelect';
import { CountrySelect } from './controls/CountrySelect';
import { CountryMultiSelect } from './controls/CountryMultiSelect';
import { MarketRegionMultiSelect } from './controls/MarketRegionMultiSelect';
import { useAppDispatch } from '../../../app/hooks';
import { Localized, useLocalization } from '@fluent/react';
import { showError, showSuccess } from '../../notifications/notificationsSlice';
import { MARKET_REGIONS } from './marketRegion';
import { CERTIFICATION_LABEL, CertificationStandardEnum } from './companyCertificates';
import { COMPANY_CONSENT_TEXT, CompanyConsentTypeEnum } from './enums/companyConsentEnum';
import { SaveButton } from '../../../app/components/SaveButton';
import { LoadingButton } from '@mui/lab';
import { SocialPlatformEnum, SocialPlatformLabel } from './enums/socialPlatformEnum';
import { EnumMultiSelect } from '../../../app/components/EnumMultiSelect';
import { QuickTagEnum, QUICK_TAG_OPTIONS, QUICK_TAG_MAP } from './enums/QuickTagEnum';
import CompanyProfileCompanySizeMenu from './controls/CompanyProfileCompanySizeMenu';

const WIZARD_CONTENT_MIN_HEIGHT = 560; // dopasuj w razie potrzeby

type Draft = CompanyProfile;

type Props = {
    open: boolean;
    value: CompanyProfile;
    onClose: () => void;
    onSuccessfulSave: (updated: CompanyProfile) => void;
    initialStep?: number;
};

const STEPS = ['Basic', 'Contact', 'Headquarters', 'Social Media', 'Markets', 'Certificates', 'Marketplace'] as const;

const SOCIAL_COLOR: Record<number, string> = {
    [SocialPlatformEnum.LinkedIn]: "#0A66C2",
    [SocialPlatformEnum.X]: "#000000",
    [SocialPlatformEnum.YouTube]: "#FF0000",
    [SocialPlatformEnum.Facebook]: "#1877F2",
    [SocialPlatformEnum.Instagram]: "#E1306C",
    [SocialPlatformEnum.Reddit]: "#FF4500",
};

const getIconColor = (platformId: number, hasUrl: boolean, theme: any) => {
    const base = SOCIAL_COLOR[platformId] ?? theme.palette.text.secondary;
    return hasUrl ? base : alpha(base, 0.5);
};

const SOCIAL_PLATFORMS = Object.entries(SocialPlatformLabel)
    .map(([idStr, label]) => ({ id: Number(idStr), label }))
    .filter(x => !Number.isNaN(x.id))
    .filter((opt, idx, arr) => arr.findIndex(o => o.id === opt.id) === idx) // dedupe po id
    .sort((a, b) => a.label.localeCompare(b.label));

// labels / opisy do enuma (na sztywno)
const CONSENT_DEFS: Array<{
    type: CompanyConsentTypeEnum;
    label: string;
    description: string;
    required: boolean;
}> = [
        {
            type: CompanyConsentTypeEnum.TermsOfService,
            label: "Regulamin (Terms of Service)",
            description: "Akceptacja regulaminu jest wymagana do publikacji profilu w marketplace.",
            required: true,
        },
        {
            type: CompanyConsentTypeEnum.PrivacyPolicy,
            label: "Polityka prywatności (Privacy Policy)",
            description: "Potwierdzenie zapoznania się z zasadami przetwarzania danych.",
            required: true,
        },
        {
            type: CompanyConsentTypeEnum.GdprDataProcessing,
            label: "Przetwarzanie danych (GDPR)",
            description: "Zgoda na przetwarzanie danych zgodnie z RODO.",
            required: true,
        },
        {
            type: CompanyConsentTypeEnum.PublicCompanyProfile,
            label: "Publiczny profil firmy",
            description: "Zgoda na publiczne wyświetlanie podstawowych informacji firmy w marketplace.",
            required: true,
        },
        {
            type: CompanyConsentTypeEnum.BrandAssetsLicense,
            label: "Licencja na materiały brandowe",
            description: "Pozwala używać logotypu/nazwy firmy w listingach i materiałach promocyjnych.",
            required: false,
        },
        {
            type: CompanyConsentTypeEnum.MarketingCommunications,
            label: "Komunikacja marketingowa",
            description: "Zgody na powiadomienia o nowościach i ofertach (opcjonalne).",
            required: false,
        },
    ];


// --- Colored Stepper icons ---
const ColorStepIcon: React.FC<StepIconProps> = (props) => {
    const { active, completed, className, icon } = props;
    const idx = Number(icon);

    const colorMap: Record<number, string> = {
        1: 'primary.main',   // Basic
        2: 'info.main',      // Contact
        3: 'success.main',   // HQ
        4: 'warning.main',   // Social
        5: 'secondary.main', // Marketplace
    };

    const icons: Record<number, React.ReactElement> = {
        1: <BusinessIcon />,
        2: <ContactMailIcon />,
        3: <HomeWorkIcon />,
        4: <ShareIcon />,
        5: <StorefrontIcon />,
        6: <VerifiedIcon />,
        7: <StorefrontIcon />,
    };

    return (
        <Box
            className={className}
            sx={{
                color: completed ? 'success.main'
                    : active ? colorMap[idx] || 'text.secondary'
                        : 'text.disabled',
                display: 'flex',
                alignItems: 'center',
            }}
        >
            {React.cloneElement(icons[idx] || <BusinessIcon />, { fontSize: 'small' })}
        </Box>
    );
};

export const CompanyProfileEditWizard: React.FC<Props> = ({
    open,
    value,
    onClose,
    onSuccessfulSave,
    initialStep = 0,
}) => {
    const dispatch = useAppDispatch();
    const { l10n } = useLocalization();

    const [activeStep, setActiveStep] = React.useState<number>(initialStep);
    const [draft, setDraft] = React.useState<Draft>(value);

    const [saving, setSaving] = React.useState(false);
    const [serverError, setServerError] = React.useState("");


    React.useEffect(() => setDraft(value), [value]);
    React.useEffect(() => { if (open) setActiveStep(initialStep); }, [open, initialStep]);

    const handleChange = <K extends keyof Draft>(key: K) =>
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const v = e.target.type === 'checkbox' ? (e.target as any).checked : e.target.value;
            setDraft(prev => ({ ...prev, [key]: v } as Draft));
        };

    // Normalize isMarketPlaceActive / IsMarketPlaceActive
    const marketActive = (draft as any).isMarketPlaceActive ?? false;
    const setMarketActive = (val: boolean) =>
        setDraft(prev => {
            const wasActive = !!(prev as any).isMarketPlaceActive;
            const next: any = { ...prev, isMarketPlaceActive: val };

            if (!wasActive && val) {
                next.restrictVisibilityToBuyers = true;
            }

            return next as Draft;
        });
    const restrictVisibilityToBuyers = (draft as any).restrictVisibilityToBuyers ?? false;
    const setRestrictVisibilityToBuyers = (val: boolean) =>
        setDraft(prev => ({ ...prev, restrictVisibilityToBuyers: val } as Draft));


    const next = () => setActiveStep(s => Math.min(s + 1, STEPS.length - 1));
    const back = () => setActiveStep(s => Math.max(s - 1, 0));

    const handleDialogClose = (_e: object, reason?: 'backdropClick' | 'escapeKeyDown') => {
        if (reason === 'backdropClick') return;
        onClose();
    };

    const handleSave = async () => {
        setSaving(true);
        setServerError("");

        // helper: wszystkie typy enuma jako liczby
        const ALL_TYPES = (Object.values(CompanyConsentTypeEnum) as Array<number | string>)
            .filter((v): v is number => typeof v === "number");

        // helper: set z typeId, które są zaznaczone (granted === true) w draft.consents
        const grantedSet = new Set<number>(
            (draft.consents ?? [])
                .filter(c => c?.typeId != null && c.granted === true)
                .map(c => c.typeId)
        );

        try {
            // 1) Walidacja wymaganych zgód, gdy marketplace włączony
            if (marketActive) {
                const missingTypeIds = CONSENT_DEFS
                    .filter(c => c.required)
                    .map(c => c.type)                       // numeric typeId z enuma
                    .filter(typeId => !grantedSet.has(typeId));

                if (missingTypeIds.length > 0) {
                    const missingLabels = CONSENT_DEFS
                        .filter(c => missingTypeIds.includes(c.type))
                        .map(c => c.label)
                        .join(", ");

                    setServerError("error-missing-required-consents");
                    setSaving(false);
                    return;
                }
            }

            // 2) Odetnij pola, których nie wysyłasz teraz (np. social)
            const { ...companyOnly } = draft as any;

            // 3) Zbuduj payload zgód:
            //    a) jeśli backend oczekuje pełnej listy z true/false:
            const consentsPayload = ALL_TYPES.map(typeId => ({
                typeId,
                granted: grantedSet.has(typeId),
            }));

            // 4) Finalny payload
            const payload: CompanyProfile & { id: string } = {
                ...companyOnly,
                isMarketPlaceActive: marketActive,
                restrictVisibilityToBuyers: restrictVisibilityToBuyers,
                consents: consentsPayload as any,
                id: (draft as any).id,
            };

            // 5) Dispatch
            const result = await dispatch(updateCompanyProfile(payload));

            if (updateCompanyProfile.fulfilled.match(result)) {
                dispatch(showSuccess("saved"));
                onSuccessfulSave(result.payload as CompanyProfile);
            } else {
                let errorMsg = "error";
                if (result.payload && result.payload.kind === "http" && result.payload.problem) {
                    errorMsg = result.payload.problem.title;
                }
                setServerError(errorMsg);
            }
        } catch (e: any) {
            dispatch(showError("error"));
            const msg =
                e?.payload?.problem?.title ??
                e?.message ??
                "Wystąpił błąd podczas zapisu.";
            setServerError(msg);
        } finally {
            setSaving(false);
        }
    };



    const canNext = true;

    return (
        <Dialog open={open}
            PaperProps={{ sx: { height: '95vh', maxHeight: '95vh', display: 'flex' } }}
            onClose={handleDialogClose} fullWidth maxWidth="md">
            <DialogTitle
                sx={{
                    pr: 6,
                    bgcolor: 'grey.100',
                    borderBottom: 1,
                    borderColor: 'divider',
                }}
            >
                Edit company profile
                <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers sx={{ minHeight: WIZARD_CONTENT_MIN_HEIGHT }}>
                <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
                    {STEPS.map(label => (
                        <Step key={label}>
                            <StepLabel StepIconComponent={ColorStepIcon}>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>

                {/* === STEP 0: Basic information (one per row, colored icons) === */}
                {activeStep === 0 && (
                    <Box>

                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    label="Company Name"
                                    fullWidth
                                    value={draft.legalName ?? ''}
                                    onChange={handleChange('legalName')}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <BusinessIcon fontSize="small" sx={{ color: 'primary.main' }} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    label="Short Name"
                                    fullWidth
                                    value={draft.shortName ?? ''}
                                    onChange={handleChange('shortName')}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <BusinessIcon fontSize="small" sx={{ color: 'primary.main' }} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>



                            <Grid item xs={12}>
                                <TextField
                                    label="About"
                                    fullWidth
                                    multiline
                                    minRows={3}
                                    value={draft.about ?? ''}
                                    onChange={handleChange('about')}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <DescriptionIcon fontSize="small" sx={{ color: 'secondary.main' }} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    label="Founded (year)"
                                    type="number"
                                    fullWidth
                                    value={draft.yearFounded ?? ''}
                                    onChange={handleChange('yearFounded')}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <CalendarMonthIcon fontSize="small" sx={{ color: 'warning.main' }} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <TimeZoneSelect
                                    value={draft.timeZone ?? ''}
                                    onChange={(val) => setDraft(prev => ({ ...prev, timeZone: val || '' }))}
                                    pinned={['Europe/Warsaw', 'UTC']}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                    <CompanyProfileCompanySizeMenu
                                        value={draft.companySizeTierId ?? ''}
                                        onChange={handleChange('companySizeTierId')}
                                    />
                            </Grid>

                            <Grid item xs={12}>
                                <Paper variant="outlined" sx={{ p: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                        <TagIcon fontSize="small" />
                                        <Typography variant="h6"><Localized id="companies-quick-tags">_Quick Tags</Localized></Typography>
                                    </Box>

                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                        <Localized id="companies-choose-quick-tags">_Choose tags that best describe your company's capabilities.</Localized>
                                    </Typography>

                                    <EnumMultiSelect
                                        sx = {{ pt: 1 }}
                                        label={l10n.getString("companies-quick-tags", null, "_Quick tags")}
                                        placeholder={l10n.getString("companies-quick-tag-placeholder", null, "_Start typing a tag…")}
                                        value={draft.quickTags?.map(m => m.quickTagId) ?? null}
                                        options={QUICK_TAG_OPTIONS.map(o => ({ id: o.id, name: o.label }))}
                                        onChange={(ids) =>
                                            setDraft(prev => {
                                                const profileIdNum = Number((prev as any).companyProfileId ?? (prev as any).id ?? 0);

                                                const numeric = ids ? ids.map(x => Number(x)) as QuickTagEnum[] : null;
                                                const newList = numeric?.map(n => {
                                                    const opt = QUICK_TAG_MAP[n];

                                                    return {
                                                        id: 0,
                                                        companyProfileId: profileIdNum,
                                                        quickTag: opt?.value,
                                                        quickTagId: n
                                                    } as CompanyQuickTag;
                                                }) ?? [];

                                                return {
                                                    ...prev,
                                                    quickTags: newList
                                                } as typeof prev;
                                            })
                                        }
                                    />
                                </Paper>
                            </Grid>
                        </Grid>
                    </Box>
                )}

                {/* === STEP 1: Contact (one-per-row, colored icons) === */}
                {activeStep === 1 && (
                    <Box>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    label="Website"
                                    fullWidth
                                    value={draft.websiteUrl ?? ''}
                                    onChange={handleChange('websiteUrl')}
                                    placeholder="https://example.com"
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <PublicIcon fontSize="small" sx={{ color: 'info.main' }} />
                                            </InputAdornment>
                                        ),
                                    }}
                                    inputProps={{ inputMode: 'url', autoComplete: 'url' }}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    label="Main email"
                                    fullWidth
                                    value={draft.primaryEmail ?? ''}
                                    onChange={handleChange('primaryEmail')}
                                    placeholder="name@company.com"
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <AlternateEmailIcon fontSize="small" sx={{ color: 'primary.main' }} />
                                            </InputAdornment>
                                        ),
                                    }}
                                    inputProps={{ inputMode: 'email', autoComplete: 'email' }}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    label="Support email"
                                    fullWidth
                                    value={draft.supportEmail ?? ''}
                                    onChange={handleChange('supportEmail')}
                                    placeholder="support@company.com"
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SupportAgentIcon fontSize="small" sx={{ color: 'secondary.main' }} />
                                            </InputAdornment>
                                        ),
                                    }}
                                    inputProps={{ inputMode: 'email', autoComplete: 'email' }}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    label="Sales email"
                                    fullWidth
                                    value={draft.salesEmail ?? ''}
                                    onChange={handleChange('salesEmail')}
                                    placeholder="sales@company.com"
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <AttachEmailIcon fontSize="small" sx={{ color: 'warning.main' }} />
                                            </InputAdornment>
                                        ),
                                    }}
                                    inputProps={{ inputMode: 'email', autoComplete: 'email' }}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    label="Phone"
                                    fullWidth
                                    value={draft.phone ?? ''}
                                    onChange={handleChange('phone')}
                                    placeholder="+48 123 456 789"
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <PhoneIcon fontSize="small" sx={{ color: 'success.main' }} />
                                            </InputAdornment>
                                        ),
                                    }}
                                    inputProps={{ inputMode: 'tel', autoComplete: 'tel' }}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    label="WhatsApp"
                                    fullWidth
                                    value={draft.whatsApp ?? ''}
                                    onChange={handleChange('whatsApp')}
                                    placeholder="+48 123 456 789 | username"
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <WhatsAppIcon fontSize="small" sx={{ color: 'success.main' }} />
                                            </InputAdornment>
                                        ),
                                    }}
                                    inputProps={{ inputMode: 'tel' }}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                )}


                {/* === STEP 2: Headquarters (country first, then city, then address section) === */}
                {activeStep === 2 && (
                    <Box>
                        <Grid container spacing={2}>
                            {/* Country (select or free text) */}
                            <Grid item xs={12}>
                                <CountrySelect
                                    value={draft.hQ_CountryName ?? ''}
                                    onChange={(name) =>
                                        setDraft(prev => ({ ...prev, hQ_CountryName: name ?? '' }))
                                    }
                                />
                            </Grid>

                            {/* City */}
                            <Grid item xs={12}>
                                <TextField
                                    label="City"
                                    fullWidth
                                    value={draft.hQ_City ?? ''}
                                    onChange={handleChange('hQ_City')}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <LocationCityIcon fontSize="small" sx={{ color: 'primary.main' }} />
                                            </InputAdornment>
                                        ),
                                    }}
                                    placeholder="e.g. Warsaw"
                                />
                            </Grid>

                            {/* Address section title (opcjonalne) */}
                            {/* <Grid item xs={12}><Typography variant="subtitle2" color="text.secondary">Address</Typography></Grid> */}

                            {/* Street */}
                            <Grid item xs={12}>
                                <TextField
                                    label="Street"
                                    fullWidth
                                    value={draft.hQ_Street ?? ''}
                                    onChange={handleChange('hQ_Street')}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <MapIcon fontSize="small" sx={{ color: 'secondary.main' }} />
                                            </InputAdornment>
                                        ),
                                    }}
                                    placeholder="e.g. Marszałkowska"
                                />
                            </Grid>

                            {/* Building no. */}
                            <Grid item xs={12}>
                                <TextField
                                    label="Building no."
                                    fullWidth
                                    value={draft.hQ_BuildingNumber ?? ''}
                                    onChange={handleChange('hQ_BuildingNumber')}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <NumbersIcon fontSize="small" sx={{ color: 'warning.main' }} />
                                            </InputAdornment>
                                        ),
                                    }}
                                    placeholder="e.g. 10A"
                                />
                            </Grid>

                            {/* Unit */}
                            <Grid item xs={12}>
                                <TextField
                                    label="Unit"
                                    fullWidth
                                    value={draft.hQ_UnitNumber ?? ''}
                                    onChange={handleChange('hQ_UnitNumber')}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <ApartmentIcon fontSize="small" sx={{ color: 'success.main' }} />
                                            </InputAdornment>
                                        ),
                                    }}
                                    placeholder="e.g. 25"
                                />
                            </Grid>

                            {/* Postal code */}
                            <Grid item xs={12}>
                                <TextField
                                    label="Postal code"
                                    fullWidth
                                    value={draft.hQ_PostalCode ?? ''}
                                    onChange={handleChange('hQ_PostalCode')}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <MarkunreadMailboxIcon fontSize="small" sx={{ color: 'secondary.main' }} />
                                            </InputAdornment>
                                        ),
                                    }}
                                    placeholder="e.g. 00-001"
                                    inputProps={{ inputMode: 'numeric' }}
                                />
                            </Grid>

                            {/* Latitude/Longitude intentionally hidden for now */}
                        </Grid>
                    </Box>
                )}


                {/* === STEP 3: Social Media (one-per-row with icons) === */}
                {activeStep === 3 && (
                    <Box
                        sx={{
                            maxHeight: '72vh',
                            overflowY: 'auto',
                            pr: 1,
                            pt: 1,
                            WebkitOverflowScrolling: 'touch',
                        }}
                    >
                        <FormGroup>
                            <Stack spacing={1.5}>
                                {SOCIAL_PLATFORMS.map(({ id: platformId, label }) => {
                                    // odczytaj aktualny URL dla tej platformy (jeśli istnieje w draft)
                                    const currentUrl =
                                        (draft.socialLinks ?? []).find(
                                            (s: any) => s.socialPlatformId === platformId || s.socialPlatform === (SocialPlatformEnum as any)[platformId]
                                        )?.url ?? "";

                                    // wybór ikony (opcjonalnie — reszta dostanie domyślną)
                                    let icon: JSX.Element = <LinkIcon fontSize="small" />;
                                    if (platformId === SocialPlatformEnum.LinkedIn) icon = <LinkedInIcon fontSize="small" />;
                                    else if (platformId === SocialPlatformEnum.X) icon = <TwitterIcon fontSize="small" />;
                                    else if (platformId === SocialPlatformEnum.YouTube) icon = <YouTubeIcon fontSize="small" />;
                                    else if (platformId === SocialPlatformEnum.Facebook) icon = <FacebookIcon fontSize="small" />;
                                    else if (platformId === SocialPlatformEnum.Instagram) icon = <InstagramIcon fontSize="small" />;
                                    else if (platformId === SocialPlatformEnum.Reddit) icon = <RedditIcon fontSize="small" />;

                                    return (
                                        <TextField
                                            key={platformId}
                                            size="small"
                                            fullWidth
                                            label={label}
                                            value={currentUrl}
                                            placeholder={`https://...`}
                                            onChange={(e) => {
                                                const url = e.target.value;

                                                setDraft(prev => {
                                                    const profileIdNum = Number((prev as any).companyProfileId ?? (prev as any).id ?? 0);
                                                    const list = [...(prev.socialLinks ?? [])] as any[];

                                                    const enumName = (SocialPlatformEnum as any)[platformId]; // reverse mapping: 1 -> "LinkedIn"
                                                    const idx = list.findIndex(
                                                        (s: any) => s.socialPlatformId === platformId || s.socialPlatform === enumName
                                                    );

                                                    // jeśli pole puste -> usuń wpis dla tej platformy
                                                    if (!url.trim()) {
                                                        if (idx >= 0) {
                                                            list.splice(idx, 1);
                                                        }
                                                        return { ...prev, socialLinks: list } as typeof prev;
                                                    }

                                                    // jeżeli istnieje — aktualizuj url
                                                    if (idx >= 0) {
                                                        const item = { ...list[idx], url };
                                                        // dopilnuj zgodności źródła prawdy po ID
                                                        item.socialPlatformId = platformId;
                                                        item.socialPlatform = enumName;
                                                        list[idx] = item;
                                                    } else {
                                                        // dodaj nowy
                                                        list.push({
                                                            id: 0, // backend nada właściwe ID
                                                            companyProfileId: profileIdNum,
                                                            socialPlatform: enumName,
                                                            socialPlatformId: platformId,
                                                            url,
                                                        });
                                                    }

                                                    return { ...prev, socialLinks: list } as typeof prev;
                                                });
                                            }}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start" sx={(theme) => ({
                                                        color: getIconColor(platformId, !!currentUrl.trim(), theme),
                                                    })}>
                                                        {icon}
                                                    </InputAdornment>
                                                ),
                                                endAdornment: currentUrl ? (
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            size="small"
                                                            component="a"
                                                            href={currentUrl}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            edge="end"
                                                            aria-label={`Open ${label}`}
                                                            onMouseDown={(e) => e.preventDefault()}
                                                        >
                                                            <OpenInNewIcon fontSize="inherit" />
                                                        </IconButton>
                                                    </InputAdornment>
                                                ) : undefined,
                                            }}
                                        />
                                    );
                                })}
                            </Stack>
                        </FormGroup>
                    </Box>
                )}
                {/* === STEP 4: Markets (wybór regionów) === */}
                {activeStep === 4 && (
                    <Stack direction="column" spacing={1} padding={1}>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <PublicIcon fontSize="small" />
                                <Typography variant="h6"><Localized id="companies-markets-by-region">_Markets by region</Localized></Typography>
                            </Box>

                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                <Localized id="companies-markets-select-regions">_Select regions where your company operates/sells.</Localized>
                            </Typography>

                            <Divider sx={{ mb: 1 }} />
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                <Localized id="companies-markets-regions-label">_Markets (Regions)</Localized>
                            </Typography>
                            <Box
                                sx={{
                                    maxHeight: '36vh',
                                    overflowY: 'auto',
                                    pr: 1,
                                    WebkitOverflowScrolling: 'touch',
                                }}
                            >
                                <MarketRegionMultiSelect
                                    value={draft.markets ?? []}
                                    onChange={(markets) => setDraft(prev => ({ ...prev, markets } as Draft))}
                                    companyProfileId={Number((draft as any).companyProfileId ?? (draft as any).id ?? 0)}
                                />
                            </Box>
                        </Paper>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <PublicIcon fontSize="small" />
                                <Typography variant="h6"><Localized id="companies-markets-by-country">_Markets by country</Localized></Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                <Localized id="companies-markets-select-country">_Select countries where your company operates/sells.</Localized>
                            </Typography>
                            <CountryMultiSelect
                                sx={{ pt: 1 }}
                                label={l10n.getString("companies-markets-countries-label", null, "_Markets (Countries)")}
                                value={draft.countryMarkets?.map(m => m.countryName) ?? null}
                                onChange={(countries, countryOptions) =>
                                    setDraft(prev => {
                                        const profileIdNum = Number(
                                            (prev as any).companyProfileId ?? (prev as any).id ?? 0
                                        );

                                        const uniqueCountries = [...new Set(countryOptions ?? [])];

                                        const newList: CompanyCountryMarket[] = uniqueCountries.map(countryOption => ({
                                            id: 0, // backend nada prawdziwe ID
                                            companyProfileId: profileIdNum,
                                            countryCode: countryOption.code,
                                            countryName: countryOption.name,
                                        }));

                                        return {
                                            ...prev,
                                            countryMarkets: newList
                                        } as typeof prev;
                                    })
                                }
                            />
                        </Paper>
                    </Stack>
                )}

                {/* === STEP 5: Certificates (wybór certyfikatów) === */}
                {activeStep === 5 && (
                    <Paper variant="outlined" sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <VerifiedIcon fontSize="small" />
                            <Typography variant="h6">Certificates</Typography>
                        </Box>

                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Select the standards/certifications your company holds.
                        </Typography>

                        <Divider sx={{ mb: 1 }} />
                        <Box
                            sx={{
                                maxHeight: '60vh',
                                overflowY: 'auto',
                                pr: 1,
                                WebkitOverflowScrolling: 'touch',
                            }}
                        >
                            <CertificatesMultiSelect
                                value={draft.certificates ?? []}
                                onChange={(certificates) => setDraft(prev => ({ ...prev, certificates } as Draft))}
                                companyProfileId={Number((draft as any).id ?? (draft as any).companyProfileId ?? 0)}
                            />
                        </Box>
                    </Paper>
                )}

                {/* === STEP 6: Marketplace === */}
                {activeStep === 6 && (
                    <MarketplaceConsentsEditor
                        marketActive={marketActive}
                        restrictVisibilityToBuyers={restrictVisibilityToBuyers}
                        consents={draft.consents ?? []}
                        serverError={serverError}
                        onMarketActiveChange={setMarketActive}
                        onRestrictVisibilityChange={setRestrictVisibilityToBuyers}
                        onConsentsChange={(consents) => setDraft(prev => ({ ...prev, consents } as Draft))}
                    />
                )}



            </DialogContent>

            <DialogActions>
                <Box sx={{ flex: 1 }} />

                <LoadingButton
                    loading={saving}
                    size="small"
                    variant="contained"
                    disabled={activeStep === 0}
                    startIcon={<ArrowBackIcon />}
                    onClick={back}
                >
                    <Localized id="back">Back</Localized>
                </LoadingButton>

                {activeStep < STEPS.length - 1 ? (
                    <LoadingButton
                        loading={saving}
                        size="small"
                        color="success"
                        variant="contained"
                        disabled={!canNext}
                        startIcon={<ArrowForwardIcon />}
                        onClick={next}
                    >
                        <Localized id="next">Next</Localized>
                    </LoadingButton>

                ) : (
                    <SaveButton
                        onClick={handleSave}
                        loading={saving}
                    ></SaveButton>
                )}
            </DialogActions>
        </Dialog >
    );
};
